import {
  computed,
  Injectable,
  signal,
  Signal,
  WritableSignal,
} from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { Observable } from "rxjs";
import { CompletePathwayDto } from "../../api/models";

/**
 * Pathway status types matching backend model
 * @const
 */
export const PATHWAY_STATUS = {
  DRAFT: 'draft',
  ACTIVE: 'active',
  PAUSED: 'paused',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
} as const;

/** Type for pathway status */
export type PathwayStatus = typeof PATHWAY_STATUS[keyof typeof PATHWAY_STATUS];

/**
 * Service for managing and monitoring pathway status throughout the application.
 * Provides reactive signals for status checks and centralized status management.
 * 
 * @example
 * ```typescript
 * // In component
 * readonly pathwayStatus = inject(PathwayStatusService);
 * 
 * // In template
 * @if (pathwayStatus.isPaused()) {
 *   <div class="alert">Your pathway is paused</div>
 * }
 * ```
 */
@Injectable({
  providedIn: "root",
})
export class PathwayStatusService {
  /** Internal signal for pathway status */
  private readonly _pathwayStatus: WritableSignal<PathwayStatus | null> = 
    signal<PathwayStatus | null>(null);
  
  /** Internal signal for complete pathway data */
  private readonly _pathway: WritableSignal<CompletePathwayDto | null> = 
    signal<CompletePathwayDto | null>(null);

  /** Public readonly signal for pathway status */
  public readonly pathwayStatus: Signal<PathwayStatus | null> = 
    this._pathwayStatus.asReadonly();
  
  /** Public readonly signal for pathway data */
  public readonly pathway: Signal<CompletePathwayDto | null> = 
    this._pathway.asReadonly();

  /**
   * Computed signal indicating if pathway is paused
   */
  public readonly isPaused: Signal<boolean> = computed(() => 
    this._pathwayStatus() === 'paused'
  );

  /**
   * Computed signal indicating if pathway is active
   */
  public readonly isActive: Signal<boolean> = computed(() => 
    this._pathwayStatus() === 'active'
  );

  /**
   * Computed signal indicating if pathway is completed
   */
  public readonly isCompleted: Signal<boolean> = computed(() => 
    this._pathwayStatus() === 'completed'
  );

  /**
   * Computed signal indicating if pathway is cancelled
   */
  public readonly isCancelled: Signal<boolean> = computed(() => 
    this._pathwayStatus() === 'cancelled'
  );

  /**
   * Computed signal indicating if pathway is in draft status
   */
  public readonly isDraft: Signal<boolean> = computed(() => 
    this._pathwayStatus() === 'draft'
  );

  /**
   * Computed signal indicating if pathway exists and is not active
   * Useful for showing warnings or alternate UI
   */
  public readonly hasNonActiveStatus: Signal<boolean> = computed(() => {
    const status = this._pathwayStatus();
    return status !== null && status !== 'active';
  });

  /**
   * Observable version of pathway status for use with guards and async operations
   */
  public readonly pathwayStatus$: Observable<PathwayStatus | null> = 
    toObservable(this._pathwayStatus);

  /**
   * Updates the pathway status from pathway data
   * @param pathway The complete pathway DTO
   */
  public updateFromPathway(pathway: CompletePathwayDto | null): void {
    this._pathway.set(pathway);
    this._pathwayStatus.set(pathway?.status || null);
  }

  /**
   * Manually sets the pathway status
   * @param status The new status to set
   */
  public setStatus(status: PathwayStatus | null): void {
    this._pathwayStatus.set(status);
  }

  /**
   * Resets the service state
   */
  public reset(): void {
    this._pathwayStatus.set(null);
    this._pathway.set(null);
  }

  /**
   * Gets a user-friendly message for the current status
   * @returns Localized status message in Italian
   */
  public getStatusMessage(): string {
    const messages: Record<PathwayStatus, string> = {
      [PATHWAY_STATUS.PAUSED]: 'Il tuo percorso è attualmente in pausa. Contatta il tuo medico per riattivarlo.',
      [PATHWAY_STATUS.COMPLETED]: 'Il tuo percorso è stato completato con successo.',
      [PATHWAY_STATUS.CANCELLED]: 'Il tuo percorso è stato annullato. Contatta il supporto per maggiori informazioni.',
      [PATHWAY_STATUS.DRAFT]: 'Il tuo percorso è in fase di preparazione.',
      [PATHWAY_STATUS.ACTIVE]: '',
    };

    const status = this._pathwayStatus();
    return status ? (messages[status] ?? '') : '';
  }

  /**
   * Gets the appropriate CSS classes for status alerts
   * @returns Tailwind CSS classes for styling
   */
  public getStatusAlertClasses(): string {
    const classes: Record<PathwayStatus, string> = {
      [PATHWAY_STATUS.PAUSED]: 'bg-yellow-50 border-yellow-200 text-yellow-800',
      [PATHWAY_STATUS.COMPLETED]: 'bg-green-50 border-green-200 text-green-800',
      [PATHWAY_STATUS.CANCELLED]: 'bg-red-50 border-red-200 text-red-800',
      [PATHWAY_STATUS.DRAFT]: 'bg-gray-50 border-gray-200 text-gray-800',
      [PATHWAY_STATUS.ACTIVE]: '',
    };

    const status = this._pathwayStatus();
    return status ? (classes[status] ?? '') : '';
  }

  /**
   * Gets an icon name for the current status
   * @returns Icon name for ng-icon
   */
  public getStatusIcon(): string {
    const icons: Record<PathwayStatus, string> = {
      [PATHWAY_STATUS.PAUSED]: 'lucidePause',
      [PATHWAY_STATUS.COMPLETED]: 'lucideCircleCheckBig',
      [PATHWAY_STATUS.CANCELLED]: 'lucideX',
      [PATHWAY_STATUS.DRAFT]: 'lucideClock',
      [PATHWAY_STATUS.ACTIVE]: 'lucideInfo',
    };

    const status = this._pathwayStatus();
    return status ? (icons[status] ?? '') : '';
  }
}